/**
 * PushHealthPanel — collapsible footer surfacing OrgWsDO's durable
 * push-queue state plus recent pool_push_errors rows for the pool.
 *
 * Fetches GET /api/admin/pools/:id/push-health on mount and whenever
 * `refreshTrigger` changes (the parent bumps it after each successful
 * apply). Default-collapsed; the summary in the header gives the at-a-
 * glance signal without opening.
 */

import { useCallback, useEffect, useState } from 'react';
import styles from './PushHealthPanel.module.css';

// Shape mirrors handleAdminGetPoolPushHealth — queue is whatever OrgWsDO
// returns for /internal/pool-push-state (schema below) plus a recentErrors
// array of pool_push_errors rows.
interface PoolPushQueue {
  targetRevision?: number;
  lastAppliedRevision?: number;
  nextAttemptAt?: number;
  attemptCount?: number;
  lastError?: string | null;
}

interface PoolPushErrorRow {
  id: string;
  poolId: string;
  revision: number;
  target: 'pool_do' | 'market_mux_do' | string;
  errorMsg: string;
  attemptedAt: string;
  resolvedAt: string | null;
}

interface PushHealthResponse {
  poolId: string;
  queue: PoolPushQueue | null;
  recentErrors: PoolPushErrorRow[];
}

export interface PushHealthPanelProps {
  poolId: string;
  authHeaders: () => Promise<Record<string, string>>;
  apiBaseUrl: string;
  refreshTrigger: number;
  onError?: (message: string) => void;
}

export function PushHealthPanel({
  poolId,
  authHeaders,
  apiBaseUrl,
  refreshTrigger,
  onError,
}: PushHealthPanelProps) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<PushHealthResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/push-health`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as PushHealthResponse;
      setData(json);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [poolId, authHeaders, apiBaseUrl, onError]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth, refreshTrigger]);

  const queue = data?.queue ?? null;
  const errors = data?.recentErrors ?? [];
  const unresolvedErrors = errors.filter((e) => !e.resolvedAt);

  const summary = buildSummary(loading, queue, unresolvedErrors.length);

  return (
    <div className={styles.panel}>
      <button type="button" className={styles.header} onClick={() => setOpen(!open)}>
        <span className={styles.title}>Push health</span>
        <span className={`${styles.summary} ${summary.cls}`}>{summary.text}</span>
      </button>
      {open && (
        <div className={styles.body}>
          <div className={styles.grid}>
            <div className={styles.cell}>
              <span className={styles.cellLabel}>Target revision</span>
              <span className={styles.cellValue}>{queue?.targetRevision ?? '—'}</span>
            </div>
            <div className={styles.cell}>
              <span className={styles.cellLabel}>Last applied</span>
              <span className={styles.cellValue}>{queue?.lastAppliedRevision ?? '—'}</span>
            </div>
            <div className={styles.cell}>
              <span className={styles.cellLabel}>Attempt count</span>
              <span className={styles.cellValue}>{queue?.attemptCount ?? '—'}</span>
            </div>
            <div className={styles.cell}>
              <span className={styles.cellLabel}>Next attempt</span>
              <span className={styles.cellValue}>{formatAttemptAt(queue?.nextAttemptAt)}</span>
            </div>
            {queue?.lastError && (
              <div className={styles.cell} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.cellLabel}>Last error</span>
                <span className={styles.cellValue} style={{ color: 'var(--color-error, #dc2626)' }}>
                  {queue.lastError}
                </span>
              </div>
            )}
          </div>

          <div>
            <div className={styles.errorsTitle}>Recent push errors ({errors.length})</div>
            {errors.length === 0 ? (
              <div className={styles.noErrors}>No recorded pool_push_errors.</div>
            ) : (
              <div className={styles.errorsList}>
                {errors.map((e) => (
                  <div key={e.id} className={`${styles.error} ${e.resolvedAt ? styles.errorResolved : ''}`}>
                    <span>rev={e.revision}</span>
                    <span>{e.target}</span>
                    <span>{relativeTime(e.attemptedAt)}</span>
                    <span style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap' }}>{e.errorMsg}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className={styles.header}
            onClick={fetchHealth}
            disabled={loading}
            style={{ alignSelf: 'flex-start', padding: 'var(--space-1) var(--space-3)', border: 'var(--border-width) solid var(--color-border)' }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      )}
    </div>
  );
}

function buildSummary(
  loading: boolean,
  queue: PoolPushQueue | null,
  unresolvedErrors: number,
): { text: string; cls: string } {
  if (loading && !queue) return { text: 'Loading…', cls: '' };
  if (!queue) return { text: 'No queue state', cls: '' };
  const target = queue.targetRevision ?? 0;
  const applied = queue.lastAppliedRevision ?? 0;
  if (unresolvedErrors > 0) {
    return { text: `${unresolvedErrors} unresolved error${unresolvedErrors === 1 ? '' : 's'}`, cls: styles.summaryError };
  }
  if (target > applied) {
    return { text: `Behind: target=${target}, applied=${applied}`, cls: styles.summaryBehind };
  }
  return { text: `Synced: rev=${applied}`, cls: styles.summaryOk };
}

function formatAttemptAt(ms?: number): string {
  if (!ms) return '—';
  const diff = ms - Date.now();
  if (diff <= 0) return 'now';
  if (diff < 60_000) return `in ${Math.floor(diff / 1000)}s`;
  if (diff < 3_600_000) return `in ${Math.floor(diff / 60_000)}m`;
  return `in ${Math.floor(diff / 3_600_000)}h`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}
